/* global Sys, Environment */

/**
 * @typedef {{x: number, y: number, width: number, height: number}} inputSegment
 */

/**
 * User input utils for global use.
 *
 * @class Sys.UserInputUtils
 * @singleton
 */
Sys.UserInputUtils = {

    /**
     * Returns the DOM element which the user is pointing at by reading the coordinates in the event object.
     *
     * @param {Object} coordinates An XY coordinate pair.
     * @returns {?HTMLElement} The element at the coordinates.
     */
    getDOMElementFromCoordinates: function(coordinates) {
        var DOMElement;

        if (Sys.isObj(coordinates) && (!Sys.isDefined(coordinates.x) || !Sys.isDefined(coordinates.y))) {
            return null;
        }

        DOMElement = document.elementFromPoint(coordinates.x, coordinates.y);

        // if the current node in the document is a text node (not an "actual"
        // HTML element, use its parent node (Node.TEXT_NODE === 3, see
        // https://developer.mozilla.org/en/nodeType)
        if (!Sys.isEmpty(DOMElement) && DOMElement.nodeType === 3) {
            DOMElement.target = DOMElement.parentNode;
        }

        return DOMElement;
    },

    /**
     * Returns the coordinates of a user interaction event.
     *
     * @param {Object} event The javascript event object.
     * @returns {{ x: number, y: number }} The coordinates of the user interaction.
     */
    getUserInputCoordinates: function(event) {
        // If it is not a touch device, the event object of a mousedown will have its
        // coordinates directly in the event object, whereas if it is a touch device
        // the coordinates will reside in a "touches" array
        // I use changedTouches array instead, because "touches" is empty for touchEnd
        var coordinates = {
            x: event.changedTouches ? event.changedTouches[0].clientX : event.clientX,
            y: event.changedTouches ? event.changedTouches[0].clientY : event.clientY
        };

        return coordinates;
    },

    /**
     * Utility function to get the DOM element from a user interaction event directly.
     *
     * @param {Object} event Event object.
     * @returns {HTMLElement} The DOM element that is at the events position.
     */
    getDOMElementFromEvent: function(event) {
        return Sys.UserInputUtils.getDOMElementFromCoordinates(Sys.UserInputUtils.getUserInputCoordinates(event));
    },

    /**
     * Calculates the metrics for the specified DOM element.
     *
     * @private
     * @param {HTMLElement} DOMElement The element for which you want metrics.
     * @returns {{top: number, left: number, width: number, height: number, scale: number}} An object with the elements metrics.
     */
    calculateMetrics: function(DOMElement) {
        var boundingRect = DOMElement.getBoundingClientRect();

        return {
            top: boundingRect.top,
            left: boundingRect.left,
            width: boundingRect.width,
            height: boundingRect.height,
            scale: DOMElement.offsetWidth / boundingRect.width
        };
    },

    /**
     * Converts window coordinates to coordinates relative to the given element.
     *
     * @param {{x: number, y: number}} windowCoordinates The window coordinates to transform.
     * @param {HTMLElement} DOMElement The element you want to use as the base.
     * @returns {{x: number, y: number}} The coordinates relative to the element.
     */
    getCoordinatesRelativeToElement: function(windowCoordinates, DOMElement) {
        var metrics = Sys.UserInputUtils.calculateMetrics(DOMElement);

        return {
            x: (windowCoordinates.x - metrics.left) * metrics.scale,
            y: (windowCoordinates.y - metrics.top) * metrics.scale
        };
    },

    /**
     * Checks recursively if the HTMLElement provided is a child of the given parent.
     *
     * @param {HTMLElement} parentElement The parent we're checking against.
     * @param {HTMLElement} childElement The possible child child element.
     * @returns {boolean} If parent and child elements are the same.
     */
    isParentAndChildElements: function(parentElement, childElement) {
        if (parentElement === childElement) {
            return true;
        }
        // do not search above the document body
        else if (childElement && childElement !== document.body && childElement.parentElement) {
            return this.isParentAndChildElements(parentElement, childElement.parentElement);
        }

        return false;
    },

    /**
     * Utility function to test if an element is the target of an input event at the given coordinates.
     *
     * @param {HTMLElement} element A DOM element.
     * @param {{x: number, y: number}} coordinates Window coordinates.
     * @returns {boolean} If element is the target of coordinates.
     */
    isCoordinateTarget: function(element, coordinates) {
        return Sys.UserInputUtils.isParentAndChildElements(element, Sys.UserInputUtils.getDOMElementFromCoordinates(coordinates));
    },

    /**
     * Utility function to test if an element is the target of a given input event.
     *
     * @param {HTMLElement} element A DOM element.
     * @param {Event} event A user input event.
     * @returns {boolean} If element is target of event.
     */
    isEventTarget: function(element, event) {
        return Sys.UserInputUtils.isParentAndChildElements(element, Sys.UserInputUtils.getDOMElementFromEvent(event));
    },

    /**
     * Checks if the coordinates are within the provided segment.
     *
     * @param {Object} coordinates The coordinates of the user input event.
     * @param {number} coordinates.x The x coordinate.
     * @param {number} coordinates.y The y coordinate.
     * @param {inputSegment|inputSegment[]} inputSegments The segment(s) to match against.
     * @param {boolean} [segmentsAreVirtual=true] Flag indicating if the segments are given in virtual coordinates.
     * @returns {boolean} Whether or not the user input matched a segment.
     */
    isUserInputInSegment: function(coordinates, inputSegments, segmentsAreVirtual) {
        var newInputSegments = inputSegments,
            newSegmentsAreVirtual = segmentsAreVirtual,
            numInputSegments,
            scale,
            x,
            y,
            currentSegment,
            i;

        if (!Sys.isDefined(coordinates) || !Sys.isDefined(newInputSegments)) {
            return false;
        }

        if (!Sys.isArray(newInputSegments)) {
            newInputSegments = [newInputSegments];
        }

        newSegmentsAreVirtual = Sys.isDefined(newSegmentsAreVirtual) ? newSegmentsAreVirtual : true;

        scale = newSegmentsAreVirtual ? Environment.determineResolution().windowToVirtualScale : 1;
        x = coordinates.x * scale;
        y = coordinates.y * scale;

        for (i = -1, numInputSegments = newInputSegments.length; ++i < numInputSegments;) {
            currentSegment = newInputSegments[i];

            if (x >= currentSegment.x && x <= currentSegment.x + currentSegment.width &&
                y >= currentSegment.y && y <= currentSegment.y + currentSegment.height) {
                return true;
            }
        }

        return false;
    }
};
